import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useQuery } from '@tanstack/react-query';
import { generateContent } from '@/utils/content';
import { AnalysisProgress } from '@/components/AnalysisProgress';
import { toast } from 'sonner';
import { LLMSContent } from '@/components/analysis/LLMSContent';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { SiteStructure } from '@/components/analysis/SiteStructure';
import { SitemapUrls } from '@/components/analysis/SitemapUrls';
import { SiteMetadataSection } from '@/components/analysis/SiteMetadata';
import { ScanHistory } from '@/components/analysis/ScanHistory';

import {
  AnalysisHistory, 
  SiteMetadata, 
  SitemapUrl,
  UrlCategories,
  formatDateTime,
  saveToFile,
  getScanHistoryFromFile,
  getMetadataFromStorage,
  saveMetadataToStorage,
  saveScanHistoryToFile,
  categorizeUrls
} from '@/utils/analysisHelpers';

const Analysis = () => {
  const { t } = useTranslation();
  const { domain } = useParams<{ domain: string }>();
  const [sitemapProgress, setSitemapProgress] = useState(0);
  const [metadataProgress, setMetadataProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [showMetadataProgress, setShowMetadataProgress] = useState(false);
  const [llmsContent, setLlmsContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [recentScans, setRecentScans] = useState<AnalysisHistory[]>([]);
  const [metadata, setMetadata] = useState<SiteMetadata | null>(null);
  const [lastScanTime, setLastScanTime] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("analysis");
  const [urlCategories, setUrlCategories] = useState<UrlCategories>({
    other: [],
    blog: [],
    products: [],
    services: [],
    about: [],
    technical: []
  });

  const saveToHistory = async (urls: SitemapUrl[]) => {
    try {
      const newEntry: AnalysisHistory = {
        domain: domain || '',
        date: new Date().toISOString(),
        urlCount: urls.length
      };
      
      const history = await getScanHistoryFromFile();
      const existingIndex = history.findIndex(item => item.domain === domain);
      let updatedHistory;
      
      if (existingIndex >= 0) {
        history[existingIndex] = newEntry;
        updatedHistory = [...history];
      } else {
        updatedHistory = [newEntry, ...history];
      }
      
      await saveScanHistoryToFile(updatedHistory);
      setRecentScans(updatedHistory);
      setLastScanTime(newEntry.date);
    } catch (error) {
      console.error('Error saving scan history:', error);
      toast.error('Tarama geçmişi kaydedilemedi');
    }
  };

  useEffect(() => {
    const loadHistory = async () => {
      const history = await getScanHistoryFromFile();
      setRecentScans(history);
      
      const lastScan = history.find(item => item.domain === domain);
      if (lastScan) {
        setLastScanTime(lastScan.date);
      }
  
      const storedMetadata = getMetadataFromStorage(domain || '');
      if (storedMetadata) {
        setMetadata(storedMetadata);
      }
    };
    
    loadHistory();
  }, [domain]);

  const fetchSiteMetadata = async () => {
    try {
      setShowMetadataProgress(true);
      setMetadataProgress(10);
      
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${domain}`)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error(t('analysis.metadataFetchError', { status: response.status, statusText: response.statusText }));
      }
      
      setMetadataProgress(40);
      const html = await response.text();
      setMetadataProgress(60);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const title = doc.querySelector('title')?.textContent || '';
      const description = doc.querySelector('meta[name="description"]')?.getAttribute('content') || '';
      const keywords = doc.querySelector('meta[name="keywords"]')?.getAttribute('content') || '';
      
      const socialMedia: SiteMetadata['socialMedia'] = {};
      const facebookLink = doc.querySelector('a[href*="facebook.com"]')?.getAttribute('href');
      const twitterLink = doc.querySelector('a[href*="twitter.com"]')?.getAttribute('content') || 
                        doc.querySelector('a[href*="x.com"]')?.getAttribute('href');
      const instagramLink = doc.querySelector('a[href*="instagram.com"]')?.getAttribute('href');
      const linkedinLink = doc.querySelector('a[href*="linkedin.com"]')?.getAttribute('href');
      const youtubeLink = doc.querySelector('a[href*="youtube.com"]')?.getAttribute('href');
      
      if (facebookLink) socialMedia.facebook = facebookLink;
      if (twitterLink) socialMedia.twitter = twitterLink;
      if (instagramLink) socialMedia.instagram = instagramLink;
      if (linkedinLink) socialMedia.linkedin = linkedinLink;
      if (youtubeLink) socialMedia.youtube = youtubeLink;
      
      const contact: SiteMetadata['contact'] = {};
      
      const emailLinks = Array.from(doc.querySelectorAll('a[href^="mailto:"]'));
      if (emailLinks.length > 0) {
        const emailHref = emailLinks[0].getAttribute('href');
        if (emailHref) {
          contact.email = emailHref.replace('mailto:', '');
        }
      }
      
      const phoneLinks = Array.from(doc.querySelectorAll('a[href^="tel:"]'));
      if (phoneLinks.length > 0) {
        const phoneHref = phoneLinks[0].getAttribute('href');
        if (phoneHref) {
          contact.phone = phoneHref.replace('tel:', '');
        }
      }
      
      const addressElement = doc.querySelector('address') || 
                            doc.querySelector('.address') || 
                            doc.querySelector('.contact-address');
      if (addressElement) {
        contact.address = addressElement.textContent?.trim();
      }
      
      const googleBusinessLink = doc.querySelector('a[href*="google.com/business"]')?.getAttribute('href') ||
                                doc.querySelector('a[href*="business.google.com"]')?.getAttribute('href');
      
      let mapLocation = null;
      const mapFrame = doc.querySelector('iframe[src*="google.com/maps"]');
      if (mapFrame) {
        const src = mapFrame.getAttribute('src');
        if (src) {
          const latLngMatch = src.match(/q=(-?\d+\.\d+),(-?\d+\.\d+)/);
          if (latLngMatch && latLngMatch.length >= 3) {
            mapLocation = {
              lat: parseFloat(latLngMatch[1]),
              lng: parseFloat(latLngMatch[2])
            };
          }
        }
      }
      
      setMetadataProgress(90);
      
      const metadataData: SiteMetadata = {
        title,
        description,
        keywords,
        socialMedia,
        contact,
        googleBusiness: googleBusinessLink || undefined,
        mapLocation: mapLocation || undefined,
        lastUpdated: new Date().toISOString()
      };
      
      setMetadata(metadataData);
      saveMetadataToStorage(domain || '', metadataData);
      
      setMetadataProgress(100);
      setTimeout(() => {
        setShowMetadataProgress(false);
      }, 500);
      
      toast.success(t('analysis.metadataUpdateSuccess'));
    } catch (error) {
      console.error(t('analysis.metadataFetchError'), error);
      toast.error(t('analysis.metadataFetchFailed'));
      setShowMetadataProgress(false);
    }
  };

  useEffect(() => {
    if (activeTab === "metadata" && (!metadata || !metadata.lastUpdated)) {
      fetchSiteMetadata();
    }
  }, [activeTab, metadata]);

  const { data: mainSitemapData, isLoading: isMainSitemapLoading } = useQuery({
    queryKey: ['main-sitemap', domain],
    queryFn: async () => {
      setShowProgress(true);
      setSitemapProgress(10);
      const sitemapUrl = `https://api.allorigins.win/raw?url=https://${domain}/sitemap.xml`;
      const response = await fetch(sitemapUrl);
      setSitemapProgress(40);
      const data = await response.text();
      
      return data;
    }
  });

  const { data: subSitemapsData, isLoading: isSubSitemapsLoading } = useQuery({
    queryKey: ['sub-sitemaps', domain, mainSitemapData],
    enabled: !!mainSitemapData && mainSitemapData.includes('<sitemapindex'),
    queryFn: async () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(mainSitemapData!, 'text/xml');
      const sitemapUrls = Array.from(doc.getElementsByTagName('loc')).map(loc => loc.textContent);
      
      const progressIncrement = 50 / (sitemapUrls.length || 1);
      setSitemapProgress(50);
      
      const allResponses = await Promise.all(
        sitemapUrls.map(async (url, index) => {
          if (!url) return null;
          const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl);
          setSitemapProgress(Math.min(50 + (index + 1) * progressIncrement, 95));
          return response.text();
        })
      );

      setSitemapProgress(100);
      setTimeout(() => setShowProgress(false), 500);
      return allResponses.filter(Boolean);
    }
  });

  const urls = React.useMemo(() => {
    if (isSubSitemapsLoading || isMainSitemapLoading) return [];
    
    const allUrls: SitemapUrl[] = [];
    
    const parseUrls = (xmlContent: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlContent, 'text/xml');
      const urlElements = doc.getElementsByTagName('url');
      
      return Array.from(urlElements).map(urlElement => ({
        loc: urlElement.getElementsByTagName('loc')[0]?.textContent || '',
        lastmod: urlElement.getElementsByTagName('lastmod')[0]?.textContent,
        changefreq: urlElement.getElementsByTagName('changefreq')[0]?.textContent,
        priority: urlElement.getElementsByTagName('priority')[0]?.textContent
      }));
    };

    if (mainSitemapData && !mainSitemapData.includes('<sitemapindex')) {
      allUrls.push(...parseUrls(mainSitemapData));
    }

    if (subSitemapsData) {
      subSitemapsData.forEach(sitemap => {
        if (sitemap) {
          allUrls.push(...parseUrls(sitemap));
        }
      });
    }

    if (allUrls.length && !showProgress) {
      saveToHistory(allUrls);
      toast.success(t('analysis.urlsAnalyzed', { count: allUrls.length }));
    }

    return allUrls;
  }, [mainSitemapData, subSitemapsData, isMainSitemapLoading, isSubSitemapsLoading, showProgress, t]);

  useEffect(() => {
    if (!urls.length) return;
    
    const categories = categorizeUrls(urls);
    setUrlCategories(categories);
    
    const content = generateContent(domain || '', categories);
    setLlmsContent(content);
  }, [domain, urls]);

  const handleRefreshScan = () => {
    window.location.reload();
  };

  const downloadLLMS = () => {
    saveToFile(llmsContent, 'llms.txt');
  };

  return (
    <>
      <Helmet>
        <title>{domain} {t('analysis.siteAnalysisReport')} - LLMS.txt</title>
        <meta name="description" content={t('analysis.metaDescription', { domain })} />
      </Helmet>

      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline" size="icon">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <h1 className="text-3xl font-bold">{domain} {t('analysis.analysisResults')}</h1>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {t('analysis.lastScan')}: {lastScanTime ? formatDateTime(lastScanTime) : t('analysis.firstScan')}
              </span>
              <Button variant="outline" size="sm" onClick={handleRefreshScan}>
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('analysis.refresh')}
              </Button>
              <Button variant="secondary" size="sm" onClick={downloadLLMS}>
                LLMS.txt İndir
              </Button>
            </div>
          </div>

          <Tabs 
            defaultValue="analysis" 
            value={activeTab}
            onValueChange={(value) => setActiveTab(value)}
          >
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="analysis">{t('analysis.siteStructure')}</TabsTrigger>
              <TabsTrigger value="metadata">{t('analysis.siteInfo')}</TabsTrigger>
              <TabsTrigger value="sitemap">{t('analysis.sitemapUrls')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="analysis">
              <SiteStructure urlCategories={urlCategories} urlsCount={urls.length} />
            </TabsContent>
            
            <TabsContent value="metadata">
              <SiteMetadataSection
                domain={domain || ''}
                metadata={metadata}
                showMetadataProgress={showMetadataProgress}
                metadataProgress={metadataProgress}
                isLoading={isMainSitemapLoading}
                formatDateTime={formatDateTime}
                fetchSiteMetadata={fetchSiteMetadata}
              />
            </TabsContent>
            
            <TabsContent value="sitemap">
              <SitemapUrls
                urls={urls}
                isLoading={isMainSitemapLoading || isSubSitemapsLoading}
                formatDateTime={formatDateTime}
                saveToFile={saveToFile}
                domain={domain || ''}
              />
            </TabsContent>
          </Tabs>
          
          <LLMSContent
            content={llmsContent}
            isEditing={isEditing}
            onEdit={setLlmsContent}
            onToggleEdit={() => setIsEditing(!isEditing)}
          />
          
          <ScanHistory 
            recentScans={recentScans}
            formatDateTime={formatDateTime}
            saveToFile={saveToFile}
          />
        </div>
      </div>

      <AnalysisProgress 
        seoProgress={sitemapProgress}
        backlinkProgress={metadataProgress}
        isVisible={showProgress || showMetadataProgress}
      />
    </>
  );
};

export default Analysis;
